import React, { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { X, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const VoiceUsersModal = ({ onClose, onUserClick }) => {
  const [voiceUsers, setVoiceUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVoiceUsers = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/voice-users`);
        setVoiceUsers(res.data);
      } catch (err) {
        console.error('Failed to fetch voice users:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVoiceUsers();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div 
        className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="voice-users-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Mic className="w-5 h-5 text-green-400" />
            Voice Feature Users
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-[#FDF2F8]/60 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-green-400 border-t-transparent rounded-full" />
          </div>
        ) : voiceUsers.length === 0 ? (
          <p className="text-[#FDF2F8]/60 text-center py-8">No users have used voice feature yet</p>
        ) : (
          <div className="space-y-3">
            {voiceUsers.map((user, idx) => (
              <div 
                key={user.user_id || idx} 
                className={`flex items-center justify-between bg-[#4A0A66]/30 rounded-lg p-3 ${onUserClick ? 'cursor-pointer hover:bg-[#4A0A66]/50 transition-colors' : ''}`}
                onClick={(e) => {
                  if (onUserClick) {
                    e.stopPropagation();
                    onUserClick(user);
                  }
                }}
                data-testid={`voice-user-row-${user.user_id || idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold">
                    {(user.name || user.email || 'U')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-medium">{user.name || 'Anonymous'}</p>
                    <p className="text-xs text-[#FDF2F8]/60">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-400">{user.transcription_count || user.click_count || 1}</p>
                  <p className="text-xs text-[#FDF2F8]/50">uses</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceUsersModal;
